import { forwardRef, ReactElement, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import styled, { useTheme } from 'styled-components';

import { WhatsonListingFragment } from '@/__generated__/graphql';
import { ComponentProps } from '@components/ComponentProps';
import WYSIWYG from "@components/WYSIWYG";
import { breakpoints, colors, container, grid, GridContainer, mediaQueries, paragraph } from '@theme/lib';
import "react-datepicker/dist/react-datepicker.css";
import FeatureCard from '../../components/FeatureCard';
import GenericCard from '../../components/GenericCard';
import Hero, { CreateHeroProps } from '../../components/Hero';
import Filter from '../../components/MultipleSelectFilter';
import Pagination from '../../components/Pagination';

import PageMeta from "@components/PageMeta";
import StyledPills from '@components/Pills';
import { useMobile } from "@lib/hooks/useMobile";
import {
  applyFilters,
  changePage,
  formatFilters,
  getAppliedFilters,
  ListingGridContainer,
  removeFilterFromSelected,
  SelectedFilters,
  StyledCardWrapper,
  StyledFilterList,
  StyledFilterWrapper,
  StyledLegendWrapper,
} from '@lib/listing';
import Link from 'next/link';
import BaseIconLegend from '@components/IconLegend';
import Icon from '@components/Icon';
import { StyledResultIcons, isIconType } from './filters';

export const StyledDateButton = styled.button`
  ${paragraph}
  display: block;
  background: ${(props) => props.theme.colors.button.secondary.bg};
  border: 1px solid ${(props) => colors.zooFont};
  border-radius: ${props => props.theme.config.buttonRadius};
  padding: 0.875rem 6rem 0.75rem 1rem;
  cursor: pointer;
  position: relative;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    height: 21px;
    width: 19.5px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    background-image: url(/WhatsOnListing/calendar-icon.svg);
  }

  ${mediaQueries.md} {
    min-width: 12rem;
    width: auto;
  }
`;

const StyledWhatsOnListing = styled.main`
  padding-bottom: 7.5rem;
  ${mediaQueries.lg} {
    padding-bottom: 12rem;
  }

  li {
    list-style: none;
  }

  .feature-card {
    margin-bottom: 2rem;

    ${mediaQueries.lg} {
      margin-bottom: 3rem;
      grid-column: 2 / span 8;
    }
  }

  .react-datepicker-popper {
    z-index: 9999;
  }
  .react-datepicker-wrapper {
    margin-bottom: 12px;
    ${mediaQueries.md} {
      margin-bottom: 0;
      margin-left: 0;
      margin-right: auto;
    }
  }
`;

const StyledCardList = styled(ListingGridContainer).attrs({ as: 'ul' })``;

const IconLegend = styled(BaseIconLegend)`
  ul {
    flex-direction: column;
    align-items: flex-start;
    ${mediaQueries.lg} {
      flex-direction: row;
      align-items: center;
    }

    li {
      span {
        ${mediaQueries.lg} {
          max-width: 142px;
        }
      }
    }
  }
`;

const StyledGenericCardWrapper = styled(StyledCardWrapper)`
  .generic-card {
    margin: auto;
    max-width: none;

    ${mediaQueries.md} {
      max-width: 100%;
    }

    ${mediaQueries.lg} {
      max-width: 590px;
    }
  }

  .generic-card__img-wrapper {
    border-radius: ${props => props.theme.config.buttonRadius};
  }
`;

const DateSelectButton = styled(StyledDateButton)`
  margin-bottom: 12px;
  ${mediaQueries.md} {
    margin-bottom: 0;
    margin-right: auto;
  }
`;

const StyledNoResult = styled.div`
  ${grid}
  ${container}
  margin-top: 80px;
  margin-bottom: 80px;
  p {
    text-align: center;
    font-weight: 400;
    font-size: 18px;
    line-height: 145%;
    color: ${(props) => props.theme.colors.text};
    a {
      font-weight: 700;
    }

    grid-column: 1 / span 5;

    ${mediaQueries.md} {
      grid-column: 2 / span 3;
    }

    ${mediaQueries.lg} {
      grid-column: 2 / span 8;
    }
  }
`;

type WhatsOnListingProps = WhatsonListingFragment & {
  pageUrl: string;
};

export default function WhatsOnListing({
  hero,
  content,
  pageUrl,
  featuredCard,
  searchFilters,
  searchResults,
  metatags,
}: ComponentProps<WhatsOnListingProps>): ReactElement<any> {
  const [datePicker, setDatePicker] = useState<JSX.Element|null>(null);
  const isMobile = useMobile(Number(breakpoints.mdLogo));
  const toggleFilters = useMobile(Number(breakpoints.lg));
  const resultSection = useRef<HTMLDivElement|null>(null);
  const heroProps = hero ? CreateHeroProps(hero) : null;
  // Applied filters are the filters currently applied in the url. Only recompute this value if searchFilters change
  const appliedFilters = useMemo(
    () =>
      searchFilters
        ? getAppliedFilters(pageUrl, searchFilters.map((searchFilter) => searchFilter.name))
        : {},
    [searchFilters, pageUrl]
  );
  const { key } = useTheme();

  // Selected filters are the filters the user has selected but not yet applied
  const [selectedFilters, setSelectedFilters] = useState(appliedFilters);
  const [pills, setPills] = useState(Object.entries(appliedFilters).map(([key, value], index) => [...value]).flat());
  const [filtersCleared, setFiltersCleared] = useState(() => false);
  useEffect(() => {
    setFiltersCleared(localStorage.getItem("filtersCleared") === 'true' ? true : false);
  }, []);

  const [showResult, setShowResult] = useState<boolean>(() => {
    if (searchResults && searchResults?.total && searchResults?.total > 0) {
      return true;
    }
    return false;
  });
  const [date, setDate] = useState<Date | null>(() => {
    if (pageUrl) {
      const url = new URL(pageUrl);
      const urlDate = url.searchParams.get('date');
      if (urlDate !== null) {
        return new Date(urlDate);
      }
    }
    return null;
  });

  const url = new URL(pageUrl);

  const onCheckboxChange = (filterType: string, value: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Toggle the changed value
      if (newFilters[filterType].has(value)) {
        newFilters[filterType].delete(value);
      } else {
        newFilters[filterType].add(value);
      }

      return newFilters;
    });
  };

  // When the user close the dropdown for a filter type, reset the selected filters of that filter type to what's currently applied
  const resetSelection = (filterType: string) => {
    setSelectedFilters((prev) => {
      // Make a deep copy of the previous selectedFilters state
      const newFilters: SelectedFilters = {};
      for (const key in prev) {
        newFilters[key] = new Set(prev[key]);
      }

      // Reset the filters of this filter type
      newFilters[filterType] = new Set(appliedFilters[filterType]);
      return newFilters;
    });
  };

  const removeFilter = async (filter: string) => {
    localStorage.setItem("filtersCleared", "true");
    applyFilters(pageUrl, removeFilterFromSelected(selectedFilters, filter))
  }

  const formattedFilters = formatFilters({
    href: pageUrl,
    filters: searchFilters ?? [],
    selectedFilters,
    appliedFilters,
    onCheckboxChange,
    onApply: applyFilters,
    onClose: resetSelection,
    // Omit title if want to allow multiple filters to dynamically display different names
    title: 'Filter by'
  });

  // @ts-ignore
  const CalendarPickerButton = forwardRef<HTMLButtonElement>(({ onClick, value, placeholder }, ref) => (
    <StyledDateButton className="calendar-picker-button" onClick={onClick} ref={ref}>
      <span>{value||placeholder}</span>
    </StyledDateButton>
  ));
  CalendarPickerButton.displayName = 'CalendarPickerButton';

  useEffect(() => {
    if (date !== null || (Object.keys(appliedFilters).length > 0 && appliedFilters?.tags?.size > 0) || filtersCleared === true) {
      if (resultSection.current !== null ) {
        let headerOffset = isMobile ? 110 : 180;
        let elementPosition = resultSection.current.getBoundingClientRect().top;
        let offsetPosition = elementPosition +  window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    }

    if (filtersCleared === true) {
      localStorage.removeItem('filtersCleared');
    }
  }, [date, appliedFilters, isMobile, filtersCleared]);

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={[]}
        path={pageUrl}
      />
      <StyledWhatsOnListing>
        {heroProps && <Hero {...heroProps} />}
        {content && <GridContainer><WYSIWYG id="whatson-content" {...content} /></GridContainer>}
        {featuredCard && <FeatureCard {...featuredCard} />}
        <ListingGridContainer ref={resultSection}>
          <StyledFilterWrapper>
            {(key !== 'zsl') && (datePicker ? datePicker :
            <DateSelectButton className="calendar-picker-button" onClick={async () => {
              const DatePicker = (await import('react-datepicker')).default
              const newDatePicker = <DatePicker
                dateFormat="dd/MM/yyyy"
                placeholderText="Select Date..."
                showPopperArrow={false}
                selected={date}
                startOpen={true}
                customInput={<CalendarPickerButton />}
                onChange={(d) => {
                  setDate(d);
                  if (d) {
                    // Convert date to London timezone.
                    let localDate = d.toLocaleString('en-GB', { timeZone: 'Europe/London' });

                    // Convert dd/mm/yyyy to yyyy-mm-dd.
                    localDate = localDate.split(', ')[0].split("/").reverse().join("-");

                    // Redirect to filter page.
                    url.searchParams.set('date', localDate);
                  } else {
                    url.searchParams.delete('date');
                  }
                  // Ideally these filter should be in single filter obj & ajax update the search result on filter update (useeffect api call).
                  window.location.href = url.href;
                }}
                />

                setDatePicker(newDatePicker)
              }}>
              <span>{date ? date.toJSON().slice(0,10).split('-').reverse().join('/') : 'Select Date...'}</span>
            </DateSelectButton>)}
            {formattedFilters.length === 1 && <Filter {...formattedFilters[0]} />}
            {formattedFilters.length > 1 && (
              <StyledFilterList>
                {formattedFilters.map((filter) => (
                  <li key={filter.title}>
                    <Filter {...filter} />
                  </li>
                ))}
              </StyledFilterList>
            )}
          </StyledFilterWrapper>
          {pills.length > 0 && <StyledPills items={pills} onClick={removeFilter} />}
        </ListingGridContainer>
        {showResult && searchResults && (
          <>
            {key !== 'zsl' && (
              <ListingGridContainer>
                <StyledLegendWrapper>
                  <IconLegend
                    toggle={toggleFilters}
                    toggleLabel="What's on key"
                    items={[
                      { type: 'accessibility', label: 'ACCESSIBLE' },
                      { type: 'family', label: 'FAMILY' },
                      { type: 'adult', label: 'ADULT' },
                      { type: 'education', label: 'EDUCATION' },
                      { type: 'member', label: 'GOLD MEMBERS, FELLOWS, PATRONS' },
                      { type: 'zsl-200', label: 'ZSL 200' },
                    ]}
                  />
                </StyledLegendWrapper>
              </ListingGridContainer>
            )}
            {(searchResults.resultList && searchResults.resultList.length > 0) && (
              <StyledCardList className='whatson-listing__card-list'>
                {searchResults.resultList.map((result) => (
                  <StyledGenericCardWrapper key={result.id}>
                    <GenericCard
                      {...result}
                      image={{
                        ...result.teaserImage,
                        alt: result.teaserImage.altText,
                        url: result.teaserImage.url,
                      }}
                      description={result.teaserDescription || ""}
                      eyebrow={result.teaserEyebrow ?? undefined}
                      label={undefined}
                      cardHeaderSlot={(result.filters?.length ?? 0) > 0 ? (
                        <StyledResultIcons>
                          {(result.filters ?? [])
                            .filter(isIconType)
                            .map((iconType) => (
                              <Icon
                                key={`${result.id}-${iconType}`}
                                type={iconType}
                                ariaHidden={true}
                              />
                            ))}
                        </StyledResultIcons>
                      ) : undefined}
                    />
                  </StyledGenericCardWrapper>
                ))}
              </StyledCardList>
            )}
            <Pagination
              href={pageUrl}
              totalPages={
                searchResults.totalPages ?? Math.ceil(searchResults.total / searchResults.pageSize)
              }
              currentPage={Math.max(searchResults.page + 1, 1)}
              siblingCount={3}
              onPageChange={changePage}
            />
          </>
        )}
        {!showResult && (
          <StyledNoResult>
            <p>On no! No events found for that date. <Link legacyBehavior={true} href="/plan-your-visit/events" aria-label="Reset your filter">Reset your filters</Link> to see everything we have going on.</p>
          </StyledNoResult>
        )}
      </StyledWhatsOnListing>
    </>
  );
}
